import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Icon, RiskPill, StatusBadge, ToolLogo } from '../components/atoms';
import { formatRelative } from '../lib/format';
import { isTerminal, stageFor } from '@air/shared';
import type { Status } from '@air/shared';

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const mine = useQuery({
    queryKey: ['my-requests'],
    queryFn: api.myRequests,
  });

  const recentlyApproved = useQuery({
    queryKey: ['registry', 'recently-approved'],
    queryFn: async () => (await api.registry({ bucket: 'approved', pageSize: 4 })).rows,
  });

  const requests = mine.data ?? [];
  const counts = {
    open: requests.filter((r) => !isTerminal(r.status)).length,
    approved: requests.filter(
      (r) => r.status === 'approved' || r.status === 'approved_with_conditions',
    ).length,
    needsAction: requests.filter(
      (r) =>
        r.status === 'it_clarification_requested' ||
        r.status === 'legal_clarification_requested',
    ).length,
  };

  const firstName = user?.fullName?.split(' ')[0] ?? '';

  return (
    <div className="reveal">
      <div className="hero reveal reveal-1">
        <div className="hero__overline">Welcome back{firstName ? `, ${firstName}` : ''}</div>
        <h1 className="hero__title">Your AI &amp; software requests</h1>
        <p className="hero__desc">
          Track where each request is in the IT &amp; Legal review pipeline. Submit a new tool when
          you need one, or search the registry to see if it's already approved.
        </p>
        <div className="hero__row">
          <button
            className="btn btn--lg"
            onClick={() => navigate('/requests/new')}
            style={{ background: '#fff', color: 'var(--color-navy)' }}
          >
            <Icon name="add-plus" size={14} /> Submit a Request
          </button>
          <button
            className="btn"
            onClick={() => navigate('/registry')}
            style={{
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.18)',
            }}
          >
            Browse Approved Registry
          </button>
          <div className="hero__row" style={{ marginLeft: 'auto', gap: 0 }}>
            <div className="hero__stat">
              <span className="hero__stat-value">{counts.open}</span>
              <span className="hero__stat-label">In Review</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-value">{counts.approved}</span>
              <span className="hero__stat-label">Approved</span>
            </div>
            {counts.needsAction > 0 && (
              <div className="hero__stat">
                <span className="hero__stat-value" style={{ color: 'var(--color-warm)' }}>
                  {counts.needsAction}
                </span>
                <span className="hero__stat-label">Awaiting You</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {counts.needsAction > 0 && (
        <div className="alert alert--warn reveal reveal-2" style={{ marginBottom: 22 }}>
          <Icon name="info-question" size={16} />
          <div>
            <strong>
              {counts.needsAction} request{counts.needsAction === 1 ? '' : 's'} need
              {counts.needsAction === 1 ? 's' : ''} a response from you.
            </strong>
            <div style={{ marginTop: 2, fontSize: 12 }}>
              A reviewer asked a clarifying question. Open the request to reply and keep the review
              moving.
            </div>
          </div>
        </div>
      )}

      <div className="page-headbar reveal reveal-2">
        <div>
          <div className="page-overline">My Requests</div>
          <h2 className="page-title" style={{ fontSize: 22 }}>
            All submissions ({requests.length})
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link className="btn btn--secondary btn--sm" to="/registry">
            View Registry
          </Link>
          <Link className="btn btn--primary btn--sm" to="/requests/new">
            <Icon name="add-plus" size={12} /> Submit a Request
          </Link>
        </div>
      </div>

      <div className="table-wrap reveal reveal-3">
        {mine.isLoading ? (
          <div style={{ padding: '40px 22px', textAlign: 'center' }}>
            <p className="muted">Loading…</p>
          </div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '60px 22px', textAlign: 'center' }}>
            <div
              style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-navy)', marginBottom: 6 }}
            >
              No requests yet
            </div>
            <p className="muted" style={{ fontSize: 13, maxWidth: '40ch', margin: '0 auto 16px' }}>
              When you submit a tool for approval, it'll show up here with full status visibility.
            </p>
            <Link className="btn btn--primary" to="/requests/new">
              Submit Your First Request
            </Link>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Ticket</th>
                <th>Tool</th>
                <th style={{ width: 200 }}>Status</th>
                <th style={{ width: 160 }}>Stage</th>
                <th style={{ width: 140 }}>Last Update</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/requests/${r.id}`)}>
                  <td className="mono">{r.ticketNumber}</td>
                  <td>
                    <div className="tool-name">{r.softwareName}</div>
                    <div className="tool-sub">
                      {r.vendor ? `${r.vendor} · ` : ''}
                      {r.category}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {stageLabel(r.status)}
                    </span>
                  </td>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {formatRelative(r.updatedAt)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {recentlyApproved.data && recentlyApproved.data.length > 0 && (
        <>
          <div className="page-headbar reveal reveal-3" style={{ marginTop: 40 }}>
            <div>
              <div className="page-overline">Recently Approved</div>
              <h2 className="page-title" style={{ fontSize: 22 }}>
                Already in the registry
              </h2>
              <p className="page-desc">
                Save a step — these tools are already approved for use at VIN.
              </p>
            </div>
            <Link to="/registry" className="btn btn--link">
              See all approved →
            </Link>
          </div>

          <div className="regrid reveal reveal-4">
            {recentlyApproved.data.map((r) => (
              <div
                key={r.id}
                className="regcard"
                onClick={() => navigate(`/requests/${r.id}`)}
              >
                <div className="regcard__head">
                  <ToolLogo name={r.softwareName} logoUrl={r.logoUrl} />
                  <StatusBadge status={r.status} />
                </div>
                <div>
                  <h3 className="regcard__name">{r.softwareName}</h3>
                  <p className="regcard__vendor">
                    {r.vendor ? `${r.vendor} · ` : ''}
                    {r.category}
                  </p>
                </div>
                <p className="regcard__desc">{r.description}</p>
                <div className="regcard__meta">
                  <span className="regcard__meta-key">
                    Approved {formatRelative(r.updatedAt)}
                  </span>
                  <RiskPill risk={r.risk} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function stageLabel(status: Status): string {
  if (isTerminal(status)) return 'Closed';
  const stage = stageFor(status);
  if (stage === 1) return 'IT Review';
  if (stage === 2) return 'Legal Review';
  return '—';
}
